// src/components/CalendarMain.tsx

import React, {useEffect, useState} from "react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import {useFetch} from "../../hooks/useFetch";
import {ICalendarEvent} from "../../interfaces/ICalendarEvent";
import CalendarEventEdit from "./CalendarEventEdit";
import FullCalendar from "@fullcalendar/react";
import {EventClickArg} from "@fullcalendar/core";
import deLocale from "@fullcalendar/core/locales/de";
import {
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    OutlinedInput,
    Checkbox,
    ListItemText, ListItemIcon,
    FormHelperText,
} from "@mui/material";
import {IProfile} from "../../interfaces/IUser";
import {
    useCreateCalendarEvent,
    useDeleteCalendarEvent,
    useGetCalendarEvents,
    useGetRoleSubCalendar,
    useGetSubCalendar,
    useUpdateCalendarEvent,
} from "../../hooks/data/data_calendar";
import {Box} from "@mui/system";
import {IProject} from "../../interfaces/IProject";
import CheckIcon from "@mui/icons-material/Check";

interface CalendarMainProps {
    is_b_appointment?: boolean;
    is_construction_appointment?: boolean;
    projectObject?: IProject;
    onEventChanged?: (action: "created" | "updated" | "deleted", eventId: number) => void;
    initialEventId?: number;
}

const CalendarMain: React.FC<CalendarMainProps> = ({
                                                       is_b_appointment,
                                                       is_construction_appointment,
                                                       projectObject,
                                                       onEventChanged,
                                                       initialEventId,
                                                   }) => {
    const {events, isLoading, error} = useGetCalendarEvents();
    const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [userArray, , statusCodeUser] = useFetch<IProfile[]>("/user/");
    const {subCalendarArray, isLoadingSubCalendarArray} = useGetSubCalendar();

    const {createCalendarEvent, isCreatingCalendarEvent} = useCreateCalendarEvent();
    const {updateCalendarEvent, isUpdatingCalendarEvent} = useUpdateCalendarEvent();
    const {deleteCalendarEvent, isDeletingCalendarEvent} = useDeleteCalendarEvent();

    // -------- Neuer Code für Mehrfach-Select --------
    const assignedSubCalendarNames = subCalendarArray?.map((sc) => sc.name) ?? [];
    const allNames = assignedSubCalendarNames;
    const [selectedFilters, setSelectedFilters] = useState<string[]>(allNames);

    const handleFilterChange = (e: SelectChangeEvent<string[]>) => {
        setSelectedFilters(e.target.value as string[]);
    };

    const defaultSubCalendarIds: string[] = Array.from(
        new Set(
            (projectObject?.Worker || [])
                .filter(w => w.ProjectRole.id === 6)
                .flatMap(w => w.Profile.sub_calendars)
                .map(id => id.toString())
        )
    );

    //Kalender anfangs nur von aussendienst anzeigen
    useEffect(() => {
        if (!subCalendarArray) return;
        const defaultNames = subCalendarArray
            .filter(sc => defaultSubCalendarIds.includes(sc.id.toString()))
            .map(sc => sc.name);

        setSelectedFilters(defaultNames);
    }, []);

    useEffect(() => {
        if (initialEventId != null && !isLoading && events && events.length > 0) {
            const apiEvent = events.find((e) => Number(e.id) === initialEventId);
            if (apiEvent) {
                setSelectedEvent({
                    id: apiEvent.id,
                    title: apiEvent.title,
                    start: new Date(apiEvent.startDate).toISOString(),
                    end: apiEvent.endDate ? new Date(apiEvent.endDate).toISOString() : undefined,
                    extendedProps: {
                        description: apiEvent.description,
                        who: apiEvent.who,
                        where: apiEvent.where,
                        subCalendars: apiEvent.subCalendars,
                        createdTime: apiEvent.createdTime,
                        editedTime: apiEvent.editedTime,
                        createdName: apiEvent.createdName,
                        editedName: apiEvent.editedName,
                        wholeDay: apiEvent.wholeDay,
                        links: apiEvent.links,
                    },
                });
                setDialogOpen(true);
            }
        }
    }, [initialEventId, events, isLoading]);

    const {roleSubCalendarArray, isLoadingRoleSubCalendar} = useGetRoleSubCalendar();

    const calendarEvents: ICalendarEvent[] = (events ?? []).map((apiEvent) => {
        const startDateObj = new Date(apiEvent.startDate);
        const formattedStart = !isNaN(startDateObj.getTime())
            ? startDateObj.toISOString()
            : apiEvent.startDate;

        let formattedEnd: string | undefined;
        if (apiEvent.endDate) {
            const endDateObj = new Date(apiEvent.endDate);
            formattedEnd = !isNaN(endDateObj.getTime()) ? endDateObj.toISOString() : apiEvent.endDate;
        }

        return {
            id: apiEvent.id,
            title: apiEvent.title,
            start: formattedStart,
            end: formattedEnd,
            allDay: apiEvent.wholeDay,
            extendedProps: {
                description: apiEvent.description,
                who: apiEvent.who,
                where: apiEvent.where,
                subCalendars: apiEvent.subCalendars,
                createdTime: apiEvent.createdTime,
                editedTime: apiEvent.editedTime,
                createdName: apiEvent.createdName,
                editedName: apiEvent.editedName,
                wholeDay: apiEvent.wholeDay,
                links: apiEvent.links,
            },
        };
    });

    // ---- Angepasste Filter-Logik ----
    let filteredEvents = calendarEvents.filter((event) => {
        // wenn "Alle" (alle Kalender) ausgewählt, alles anzeigen
        if (selectedFilters.length === allNames.length) return true;
        // sonst nur Termine, die in einem der ausgewählten Kalender sind
        return event.extendedProps.subCalendars.some((sub) => selectedFilters.includes(sub));
    });
    // ---------------------------------

    const handleEventClick = (clickInfo: EventClickArg) => {
        const eventApi = clickInfo.event;
        const clickedEvent: ICalendarEvent = {
            id: eventApi.id,
            title: eventApi.title,
            start: eventApi.start?.toISOString() || "",
            end: eventApi.end?.toISOString() || undefined,
            extendedProps: {
                description: eventApi.extendedProps.description ?? "",
                who: eventApi.extendedProps.who ?? "",
                where: eventApi.extendedProps.where ?? "",
                subCalendars: eventApi.extendedProps.subCalendars ?? [],
                createdTime: eventApi.extendedProps.createdTime ?? "",
                editedTime: eventApi.extendedProps.editedTime ?? null,
                createdName: eventApi.extendedProps.createdName ?? "",
                editedName: eventApi.extendedProps.editedName ?? null,
                wholeDay: eventApi.extendedProps.wholeDay ?? false,
                links: eventApi.extendedProps.links ?? [],
            },
        };
        setSelectedEvent(clickedEvent);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedEvent(null);
    };

    const convertDatetimeLocalToBackendFormat = (datetimeLocal: string): string => {
        if (!datetimeLocal) return "";
        const [date, time] = datetimeLocal.split("T");
        return `${date} ${time}:00`;
    };

    const handleSaveEvent = async (updatedEvent: ICalendarEvent) => {
        const mappedSubCalendars = updatedEvent.extendedProps.subCalendars.map((id: string) => {
            const found = subCalendarArray?.find((sc) => sc.id.toString() === id);
            return found ? found.name : id;
        });
        const payload = {
            startDate: convertDatetimeLocalToBackendFormat(updatedEvent.start),
            endDate: updatedEvent.end ? convertDatetimeLocalToBackendFormat(updatedEvent.end) : "",
            timeZone: "Europe/Berlin",
            title: updatedEvent.title,
            subCalendars: mappedSubCalendars,
            description: updatedEvent.extendedProps.description,
            who: updatedEvent.extendedProps.who,
            where: updatedEvent.extendedProps.where,
            wholeDay: updatedEvent.extendedProps.wholeDay,
            links: updatedEvent.extendedProps.links,
        };
        try {
            if (parseInt(updatedEvent.id) < 0 || updatedEvent.id === "") {
                if (is_b_appointment || is_construction_appointment) {
                    const res = await createCalendarEvent({...payload, is_b_appointment, is_construction_appointment, id_projectObject: projectObject?.id});
                    if (res && typeof (res as any).eventId === "number" && onEventChanged) {
                        onEventChanged("created", (res as any).eventId);
                    }
                } else {
                    await createCalendarEvent(payload);
                }
            } else {
                await updateCalendarEvent({id: parseInt(updatedEvent.id), payload});
                if (onEventChanged) onEventChanged("updated", parseInt(updatedEvent.id));
            }
            handleCloseDialog();
        } catch (e) {
            console.error(e);
        }
    };

    const handleDelete = async () => {
        if (selectedEvent && parseInt(selectedEvent.id) > 0) {
            await deleteCalendarEvent(parseInt(selectedEvent.id));
            if (onEventChanged) onEventChanged("deleted", parseInt(selectedEvent.id));
            handleCloseDialog();
        }
    };



    const handleDateClick = (arg: { date: Date }) => {
        const clickedDate = arg.date;
        const newEvent: ICalendarEvent = {
            id: "",
            title: "",
            start: clickedDate.toISOString(),
            end: new Date(clickedDate.getTime() + 60 * 60 * 1000).toISOString(),
            extendedProps: {
                description: "",
                who: "",
                where: "",
                subCalendars: defaultSubCalendarIds,
                createdTime: "",
                editedTime: null,
                createdName: "",
                editedName: null,
                wholeDay: false,
                links: [],
            },
        };
        setSelectedEvent(newEvent);
        setDialogOpen(true);
    };

    if (
        isLoading ||
        !statusCodeUser ||
        !userArray ||
        !subCalendarArray ||
        isLoadingSubCalendarArray ||
        isLoadingRoleSubCalendar ||
        isCreatingCalendarEvent ||
        isUpdatingCalendarEvent ||
        isDeletingCalendarEvent
    ) {
        return <LinearProgress/>;
    }
    if (statusCodeUser !== 200) {
        return <div>Fehler beim Laden der Termine</div>;
    }

    return (
        <div style={{margin: "20px"}}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <FormControl
                variant="outlined"
                sx={{minWidth: 200}}
                error={selectedFilters.length === 0}
            >
                <InputLabel id="filter-subcalendar-label">Kalender</InputLabel>
                <Select
                    labelId="filter-subcalendar-label"
                    label="Kalender"
                    multiple
                    value={selectedFilters}
                    onChange={handleFilterChange}
                    input={<OutlinedInput label="Kalender"/>}
                    renderValue={(sel) => {
                        if (sel.length === allNames.length) return "Alle";
                        if (sel.length === 1) return sel[0];
                        return "Benutzerdefiniert";
                    }}
                >
                    {allNames.map((name) => {
                        const isSelected = selectedFilters.includes(name);
                        return (
                            <MenuItem
                                key={name}
                                value={name}
                                selected={isSelected}
                                sx={{
                                    "&.Mui-selected": {
                                        backgroundColor: "rgba(76, 175, 80, 0.2)",
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 32,
                                        color: isSelected ? "success.main" : "transparent",
                                    }}
                                >
                                    <CheckIcon fontSize="small"/>
                                </ListItemIcon>
                                <ListItemText primary={name}/>
                            </MenuItem>
                        );
                    })}
                </Select>
                {selectedFilters.length === 0 && (
                    <FormHelperText>Bitte mind. einen Kalender auswählen</FormHelperText>
                )}
            </FormControl>
            </Box>

            <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                locale={deLocale}
                initialView="timeGridWeek"
                headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                }}
                events={filteredEvents}
                eventTimeFormat={{hour: "2-digit", minute: "2-digit", hour12: false}}
                eventDidMount={(info) => {
                    const subs = info.event.extendedProps.subCalendars || [];
                    let bgColor = "#6fa2fc";
                    if (subs.length === 1) {
                        const found = subCalendarArray.find((sc) => sc.name === subs[0]);
                        if (found?.calendar_color) bgColor = found.calendar_color;
                    } else if (subs.length > 1) {
                        bgColor = "#0059ff";
                    }
                    info.el.style.backgroundColor = bgColor;
                }}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                firstDay={1}
                height="auto"
                dayMaxEvents={10}
            />
            <CalendarEventEdit
                open={dialogOpen}
                event={selectedEvent}
                onClose={handleCloseDialog}
                onSave={handleSaveEvent}
                onDelete={handleDelete}
                userArray={userArray}
                subCalendarArray={subCalendarArray}
                initialSubCalendars={defaultSubCalendarIds}
            />
        </div>
    );
};

export default CalendarMain;
